import type { ReactNode } from "react";

import {
  AereaMaritimaDashboard,
  CotizadorAereoSection,
  DisponibilidadSection,
  ReservasSection,
} from "./sections/aereaMaritima";
import {
  EmbarcadorPanel,
  PublicarCargaSection,
  MisCargasSection,
  ViajesEmbarcadorSection,
  TransportistasSection,
  VerificacionEmbarcadorSection,
} from "./sections/embarcador";
import {
  TransportistaPanel,
  VerificacionTransportistaSection,
  BolsaCargasSection,
  CapacidadSection,
  ViajesTransportistaSection,
} from "./sections/transportista";
import { VerificacionesAdminSection, ComisionesAdminSection } from "./sections/administracion";
import { AltaTransportistasAdminSection } from "./sections/altaTransportistas";
import { TOP_ROLE_IDS } from "../../hooks/useIsReseller";

// ---------------------------------------------------------------------------
// Single source of truth for the Cargo Market navigation tree. Drives:
//   - the apps-sidebar category/apps (AppsSidebar/appsConfig.tsx)
//   - the per-functionality sub-menu (CargoMarketLayout)
//   - the nested routes (router/config.tsx via routes.tsx)
// La bolsa de carga terrestre tiene un espacio por actor de la especificación
// funcional: Embarcador (compra transporte), Transportista (vende capacidad con
// su flotilla real) y Administración (Coding Cloud: KYC y comisiones). «Carga
// aérea y marítima» sigue como cotizador multimodal aparte.
// ---------------------------------------------------------------------------

export interface NavItem {
  id: string; // route segment under the functionality
  label: string;
  icon: string; // remixicon class for the sub-menu
  element: ReactNode;
}

export interface Functionality {
  slug: string; // route base: /cargo-market/<slug>
  name: string;
  description: string;
  iconClass: string; // apps-sidebar icon
  color: string; // tailwind bg color for the app tile
  remixIcon: string; // sub-menu / header icon
  menu: NavItem[];
  /** Sólo estos roles ven la funcionalidad (Administración = plataforma). */
  requiredRoleIds?: string[];
}

export const FUNCTIONALITIES: Functionality[] = [
  {
    slug: "embarcador",
    name: "Embarcador",
    description: "Publica cargas, recibe ofertas de transportistas verificados y sigue tus viajes",
    iconClass: "ri-box-3-line",
    color: "bg-amber-500",
    remixIcon: "ri-box-3-line",
    menu: [
      { id: "panel", label: "Panel", icon: "ri-dashboard-line", element: <EmbarcadorPanel /> },
      { id: "publicar", label: "Publicar carga", icon: "ri-add-box-line", element: <PublicarCargaSection /> },
      { id: "cargas", label: "Mis cargas y ofertas", icon: "ri-file-list-3-line", element: <MisCargasSection /> },
      { id: "viajes", label: "Viajes", icon: "ri-route-line", element: <ViajesEmbarcadorSection /> },
      { id: "transportistas", label: "Transportistas", icon: "ri-shield-user-line", element: <TransportistasSection /> },
      { id: "verificacion", label: "Mi empresa y verificación", icon: "ri-shield-check-line", element: <VerificacionEmbarcadorSection /> },
    ],
  },
  {
    slug: "transportista",
    name: "Transportista",
    description: "Verifica tu flotilla, toma cargas, publica capacidad y retornos y ejecuta viajes",
    iconClass: "ri-truck-line",
    color: "bg-emerald-500",
    remixIcon: "ri-truck-line",
    menu: [
      { id: "panel", label: "Panel", icon: "ri-dashboard-line", element: <TransportistaPanel /> },
      { id: "bolsa", label: "Bolsa de cargas", icon: "ri-store-2-line", element: <BolsaCargasSection /> },
      { id: "capacidad", label: "Mi capacidad y retornos", icon: "ri-roadster-line", element: <CapacidadSection /> },
      { id: "viajes", label: "Mis viajes", icon: "ri-route-line", element: <ViajesTransportistaSection /> },
      { id: "verificacion", label: "Verificación (KYC)", icon: "ri-shield-check-line", element: <VerificacionTransportistaSection /> },
    ],
  },
  {
    slug: "aerea-maritima",
    name: "Carga aérea y marítima",
    description: "Cotizador multimodal y compra de espacio de carga vía proveedor",
    iconClass: "ri-plane-line",
    color: "bg-sky-500",
    remixIcon: "ri-plane-line",
    menu: [
      { id: "dashboard", label: "Dashboard", icon: "ri-dashboard-line", element: <AereaMaritimaDashboard /> },
      { id: "cotizador", label: "Cotizador multimodal", icon: "ri-calculator-line", element: <CotizadorAereoSection /> },
      { id: "disponibilidad", label: "Disponibilidad y tarifas", icon: "ri-search-eye-line", element: <DisponibilidadSection /> },
      { id: "reservas", label: "Reservas y booking", icon: "ri-shopping-cart-2-line", element: <ReservasSection /> },
    ],
  },
  {
    slug: "administracion",
    name: "Administración del mercado",
    description: "Da de alta transportistas, valida documentos de transportistas y embarcadores y controla las comisiones",
    iconClass: "ri-scales-3-line",
    color: "bg-indigo-500",
    remixIcon: "ri-scales-3-line",
    requiredRoleIds: TOP_ROLE_IDS,
    menu: [
      { id: "verificaciones", label: "Verificaciones", icon: "ri-file-shield-2-line", element: <VerificacionesAdminSection /> },
      { id: "comisiones", label: "Comisiones", icon: "ri-percent-line", element: <ComisionesAdminSection /> },
      { id: "transportistas", label: "Alta de transportistas", icon: "ri-truck-line", element: <AltaTransportistasAdminSection /> },
    ],
  },
];

export const CARGO_MARKET_BASE = "/cargo-market";

/**
 * Rutas que ya no existen (maquetas de subastas reemplazadas por la bolsa de
 * carga): se redirigen al espacio equivalente para no romper enlaces.
 */
export const LEGACY_REDIRECTS: Record<string, string> = {
  terrestre: "embarcador/panel",
  "terrestre/dashboard": "embarcador/panel",
  "terrestre/cotizador": "embarcador/publicar",
  "terrestre/mercado": "transportista/bolsa",
  "terrestre/mis-ofertas": "embarcador/cargas",
  "rutas-retorno": "transportista/capacidad",
  "rutas-retorno/*": "transportista/capacidad",
  carriers: "embarcador/transportistas",
  "carriers/*": "embarcador/transportistas",
};

/** Default landing route for the whole section (first functionality dashboard). */
export const cargoMarketDefaultRoute = `${CARGO_MARKET_BASE}/${FUNCTIONALITIES[0].slug}/${FUNCTIONALITIES[0].menu[0].id}`;

/** Route to a functionality's first menu item (used by the apps-sidebar tiles). */
export const functionalityRoute = (f: Functionality) =>
  `${CARGO_MARKET_BASE}/${f.slug}/${f.menu[0].id}`;
